import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SettingsAddress } from 'icreate/common/models/alarm-group';
import { SensorTagSlot } from 'icreate/common/models/sensor-cable-tag';
import { Vessel } from 'icreate/common/models/vessel';
import { DutyEngineerView } from 'icreate/common/models/duty-engineer';
import { DutyEngineerControlMode } from 'icreate/common/models/duty-engineer-settings';
import { dutyEngineerValidator } from 'icreate/common/validators/duty-engineer/duty-engineer-validator';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-add-duty-engineer',
    templateUrl: './add-duty-engineer.component.html',
    styleUrls: ['./add-duty-engineer.component.css'],
    standalone: false,
})
export class AddDutyEngineerComponent implements OnInit {
    readonly ACCOUNT_NAME = 'name';
    readonly ACCOUNT_ABBREVIATION = 'abbreviation';
    readonly FU_NO_SELECTION = 'No selection';
    readonly DutyEngineerControlMode = DutyEngineerControlMode;

    name: string = '';
    abbreviation: string = '';
    nameIsRequired: boolean = false;
    nameAlreadyExists: boolean = false;
    abbreviationIsRequired: boolean = false;
    abbreviationAlreadyExists: boolean = false;
    isLoading = false;
    vesselSlots: SensorTagSlot[] = [];
    settingAddress: SettingsAddress | null = null;

    originalName: string = '';
    originalAbbreviation: string = '';
    originalSettingAddress: SettingsAddress | null = null;
    validationErrors: ValidationErrorMap = {};
    hasError: boolean;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            mode: boolean;
            settingId: string;
            engineer?: DutyEngineerView;
            controlType: DutyEngineerControlMode;
            vessel: Vessel;
            vesselSlots: SensorTagSlot[];
            engineerList: DutyEngineerView[];
        },
        private dialogRef: MatDialogRef<AddDutyEngineerComponent>,
        private translateService: TranslateService
    ) {}

    async ngOnInit() {
        this.isLoading = true;
        this.vesselSlots = this.data.vesselSlots ?? [];

        if (this.data.mode && this.data.engineer) {
            this.originalName = this.data.engineer.name ?? '';
            this.originalAbbreviation = this.data.engineer.abbreviation ?? '';
            this.originalSettingAddress = this.parseInputAddress(this.data.engineer.inputAddress);

            this.name = this.originalName;
            this.abbreviation = this.originalAbbreviation;
            this.settingAddress = this.originalSettingAddress;
        }

        this.updateValidationErrors();
        this.isLoading = false;
    }

    onInputChange(event: { name: string; value: string }) {
        if (event.name === this.ACCOUNT_NAME) {
            this.name = event.value;
        } else if (event.name === this.ACCOUNT_ABBREVIATION) {
            this.abbreviation = event.value;
        }
        this.updateValidationErrors();
    }

    onSelectorChange(address: SettingsAddress) {
        this.settingAddress = address;
        this.updateValidationErrors();
    }

    updateValidationErrors(): void {
        const validationData = {
            settingId: this.data.settingId,
            name: this.name.trim(),
            abbreviation: this.abbreviation.trim(),
            fcuNum: this.settingAddress?.fcuNum ?? null,
            fuNum: this.settingAddress?.fuNum ?? null,
            slotNum: this.settingAddress?.slotNum ?? null,
            terminal: this.settingAddress?.terminal ?? null,
        };

        const trimmedName = this.name?.trim();
        this.nameAlreadyExists =
            !!trimmedName &&
            this.data.engineerList.some(
                engineer => engineer.name === this.name?.trim() && engineer.id !== this.data.engineer?.id
            );

        const trimmedAbbreviation = this.abbreviation?.trim();
        this.abbreviationAlreadyExists =
            !!trimmedAbbreviation &&
            this.data.engineerList.some(
                engineer =>
                    engineer.abbreviation === this.abbreviation?.trim() && engineer.id !== this.data.engineer?.id
            );

        const errors = getValidationErrors(dutyEngineerValidator(), validationData);
        const mappedErrors: { [key: string]: { message: string } } = mapErrors(errors) || {};

        if (this.nameAlreadyExists) {
            mappedErrors['name'] = {
                message: this.translateService.instant(
                    'duty-engineer.add-duty-engineer.dialog.name.error.already-exists'
                ),
            };
        }
        if (this.abbreviationAlreadyExists) {
            mappedErrors['abbreviation'] = {
                message: this.translateService.instant(
                    'duty-engineer.add-duty-engineer.dialog.abbreviation.error.already-exists'
                ),
            };
        }

        this.validationErrors = Object.keys(mappedErrors).length > 0 ? mappedErrors : null;
        this.hasError = Object.keys(mappedErrors).length > 0;
    }

    isCreatingDisabled(): boolean {
        if (this.hasError) return true;

        if (this.data.mode) {
            const nameChanged = this.name !== this.originalName;
            const abbreviationChanged = this.abbreviation !== this.originalAbbreviation;
            const addressChanged = JSON.stringify(this.settingAddress) !== JSON.stringify(this.originalSettingAddress);
            return !nameChanged && !abbreviationChanged && !addressChanged;
        }
        return false;
    }

    cancelClick() {
        this.dialogRef.close(false);
    }

    createButtonClick() {
        if (this.isCreatingDisabled()) return;

        const address = this.settingAddress;
        const matchingSlot = address
            ? this.vesselSlots.find(
                  s =>
                      s.fcuNum === address.fcuNum &&
                      (s.fuNum ?? null) === (address.fuNum ?? null) &&
                      s.no === address.slotNum
              )
            : null;

        this.dialogRef.close({
            name: this.name,
            abbreviation: this.abbreviation,
            inputAddress: this.formatInputAddress(address),
            fcuId: matchingSlot?.fcuId ?? null,
            fuId: matchingSlot?.fuId ?? null,
            slotId: matchingSlot?.slotId ?? null,
        });
    }

    private parseInputAddress(inputAddress: string): SettingsAddress | null {
        if (!inputAddress) return null;
        const parts = inputAddress.split(', ');
        const fcuNo = parts[0] ?? '';
        const fuNo = parts[1] ?? '';
        const slot = parts[2] ?? '';
        const terminal = parts[3] ?? '';

        const fcuNum = fcuNo ? parseInt(fcuNo.replace('FCU', '')) : NaN;
        if (isNaN(fcuNum)) return null;

        return {
            fcuNum,
            fuNum: fuNo && fuNo !== this.FU_NO_SELECTION ? parseInt(fuNo.replace('FU', '')) : null,
            slotNum: slot ? parseInt(slot.replace('Slot ', '')) : NaN,
            terminal: terminal ? parseInt(terminal.replace('Terminal ', '')) : NaN,
        };
    }

    private formatInputAddress(address: SettingsAddress | null): string {
        if (!address || isNaN(address.fcuNum)) return '';
        const fcuPart = `FCU${address.fcuNum}`;
        const fuPart = address.fuNum != null ? `FU${address.fuNum}` : this.FU_NO_SELECTION;
        const slotPart = address.slotNum != null ? String(address.slotNum).padStart(2, '0') : '';
        const terminalPart = address.terminal != null ? String(address.terminal) : '';
        return [fcuPart, fuPart, slotPart, terminalPart].filter(p => p).join(', ');
    }
}
